import { Page } from "components/page/page.component"
import { View } from "components/view/view.component"
import style from '../roomOccupationReport/roomOccupationReport.module.css'
import { useState } from "react"
import { getOneMonthBefore } from "utils/date.util"
import { Button } from "components/button/button.component"
import { Icon } from "components/icon/icon.component"
import { Filters } from "components/filters/filters.component"
import { FilterType } from "model/filter.model"
import { useGetRoomOccupationReportApi } from "api/reports.api"
import { downloadPDFReportFile } from "utils/pdfReportHook.util"
import { downloadExcelReportFile } from "utils/excelReportHook.util"
import { createRoomOccupationReportData } from "reports/roomOccupation.data.report"
import { Alert } from "components/alert/alert.component"
import { RoomOccupationReport } from "model/reports/roomOccupation.report.model"

type OccupationPerMonthAdminReportPageProps = {
    companyId?: string
}

type WeekInterval = {
    start: Date
    end: Date
}

function getWeekIntervalsForMonth(date: Date): WeekInterval[] {
    const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0)
    const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999)

    const intervals: WeekInterval[] = []
    let currentStart = new Date(startOfMonth)

    while (currentStart.getTime() <= endOfMonth.getTime()) {
        const currentEnd = new Date(currentStart)
        currentEnd.setDate(currentEnd.getDate() + 6)
        currentEnd.setHours(23, 59, 59, 999)

        if (currentEnd.getTime() > endOfMonth.getTime()) {
            currentEnd.setTime(endOfMonth.getTime())
        }

        intervals.push({ start: new Date(currentStart), end: new Date(currentEnd) })

        currentStart = new Date(currentEnd)
        currentStart.setDate(currentStart.getDate() + 1)
        currentStart.setHours(0, 0, 0, 0)
    }

    return intervals
}

export function OccupationPerMonthAdminReportPage({companyId}: OccupationPerMonthAdminReportPageProps){
    const [filter, setFilter] = useState<FilterType | undefined>()
    const [loading, setLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | undefined>()

    const { get } = useGetRoomOccupationReportApi()

    function handleOnFilterChange(filter: FilterType){
        setErrorMessage(undefined)
        setFilter(filter)
    }

    function validateResponse(data: any): RoomOccupationReport[]{
        if(!Array.isArray(data)) return []
        const result: RoomOccupationReport[] = []
        for(let i = 0; i < data.length; i++){
            const item = data[i]
            if(!item || typeof item !== 'object') continue
            const room = typeof item.room === 'string' ? item.room : undefined
            if(!room) continue
            const dateStr = typeof item.date === 'string' ? item.date : undefined
            const date = dateStr ? new Date(dateStr) : undefined
            const occupied = (item.occupied === undefined || item.occupied === null)
                ? undefined
                : Boolean(item.occupied)
            result.push({
                building: typeof item.building === 'string' ? item.building : undefined,
                area: typeof item.area === 'string' ? item.area : undefined,
                room,
                date,
                occupied
            })
        }
        return result
    }

    async function getData(): Promise<RoomOccupationReport[] | undefined>{
        if(!companyId) return
        if(!filter?.date) return
        if(!filter?.rooms) return

        const intervals = getWeekIntervalsForMonth(filter.date)
        const aggregated: RoomOccupationReport[] = []

        for(const interval of intervals){
            try {
                const partial = await get({
                    rooms: filter.rooms,
                    startDate: interval.start,
                    endDate: interval.end
                }, validateResponse)
                
                if(partial && partial.length > 0){
                    // Usar apply em lotes para evitar stack overflow
                    const batchSize = 10000
                    for(let i = 0; i < partial.length; i += batchSize){
                        const batch = partial.slice(i, i + batchSize)
                        aggregated.push.apply(aggregated, batch)
                    }
                }
            } catch (e) {
                console.warn('Erro ao buscar dados da semana:', e)
            }
        }

        return aggregated
    }

    async function downloadExcelReport(){
        setErrorMessage(undefined)
        setLoading(true)
        try {
            const data = await getData()
            if(!data) throw new Error('No data received')
            const reportData = createRoomOccupationReportData(data)
            await downloadExcelReportFile(reportData, 'AROTech - Relatorio de ocupacao de salas', 'Relatorio de ocupacao de salas')
        } catch (error) {
            console.error('Error downloading Excel report:', error)
            setErrorMessage('Erro ao gerar o relatório. Tente novamente ou reduzir o número de salas.')
        }
        setLoading(false)
    }

    async function downloadPDFReport(){
        setErrorMessage(undefined)
        setLoading(true)
        try {
            const data = await getData()
            if(!data) throw new Error('No data received')
            const reportData = createRoomOccupationReportData(data)
            await downloadPDFReportFile(reportData, 'AROTech - Relatorio de ocupacao de salas', 'Relatorio de ocupacao de salas')
        } catch (error) {
            console.error('Error downloading PDF report:', error)
            setErrorMessage('Erro ao gerar o relatório. Tente novamente ou reduzir o número de salas.')
        }
        setLoading(false)
    }

    return <Page menu header title="Relatório Ocupação de Salas">
        <View className={style.Content}>
            <View className={style.ContentHeader}>
                <Filters 
                    companyId={companyId} 
                    showBuildings
                    showAreas
                    showRooms 
                    showDate
                    initialDate={getOneMonthBefore(new Date())}
                    dateType="month" 
                    onChange={handleOnFilterChange}
                    roomIndicatorIds={['room-occupation-status']}
                    selectAllAreasWhenBuildingChanges
                    selectAllRoomsWhenAreaChanges
                />
            </View>
            <View className={style.ExportSection}>
                <View className={style.ExportSectionButtons}>
                    <Button variant='contained' startIcon={<Icon className={style.Icons}>picture_as_pdf</Icon>}
                        onClick={downloadPDFReport}
                        disabled={!filter || !filter.date || !filter.rooms || filter.rooms.length <= 0}
                        loading={loading}
                    >
                        Exportar para PDF
                    </Button>
                    <Button 
                        variant='contained' 
                        startIcon={<Icon className={style.Icons}>border_all</Icon>}
                        loading={loading}
                        onClick={downloadExcelReport}
                        disabled={!filter || !filter.date || !filter.rooms || filter.rooms.length <= 0}
                    >
                        Exportar para Excel
                    </Button>
                </View>
                {errorMessage && <Alert severity="error" onClose={() => {setErrorMessage(undefined) }} style={{marginTop: '50px'}}>
                    {errorMessage}
                </Alert>}
            </View>
        </View>
    </Page>
}
